import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import type { Permission } from '@/api/permissions';

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean;
    permission?: Permission;
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { requiresAuth: false },
  },
  {
    // Where a Google/Microsoft callback lands. Outside the AppShell: there is no
    // session yet when the page mounts — acquiring one is the page's whole job.
    path: '/login/sso',
    name: 'sso-callback',
    component: () => import('@/views/SsoCallbackView.vue'),
    meta: { requiresAuth: false },
  },
  {
    // The forced-enrollment screen (M6). Outside the AppShell, because a session
    // diverted here can reach almost nothing and a full navigation would be an
    // invitation to click into a dozen 403s.
    path: '/security/setup',
    name: 'mfa-setup',
    component: () => import('@/views/MfaSetupView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/forbidden',
    name: 'forbidden',
    component: () => import('@/views/ForbiddenView.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/',
    component: () => import('@/components/AppShell.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/views/DashboardView.vue'),
      },
      {
        path: 'workspaces',
        name: 'workspaces',
        component: () => import('@/views/WorkspacesView.vue'),
        meta: { permission: 'workspace:read' },
      },
      {
        path: 'workspaces/:id',
        name: 'workspace-settings',
        component: () => import('@/views/WorkspaceSettingsView.vue'),
        meta: { permission: 'workspace:read' },
        props: true,
      },
      {
        path: 'members',
        name: 'members',
        component: () => import('@/views/MembersView.vue'),
        meta: { permission: 'member:read' },
      },
      {
        path: 'user-groups',
        name: 'user-groups',
        component: () => import('@/views/UserGroupsView.vue'),
        meta: { permission: 'group:read' },
      },
      {
        path: 'media',
        name: 'media',
        component: () => import('@/views/MediaView.vue'),
        meta: { permission: 'media:read' },
      },
      {
        path: 'media/:id',
        name: 'media-detail',
        component: () => import('@/views/MediaAssetView.vue'),
        meta: { permission: 'media:read' },
        props: true,
      },
      {
        path: 'layouts',
        name: 'layouts',
        component: () => import('@/views/LayoutsView.vue'),
        meta: { permission: 'layout:read' },
      },
      {
        path: 'layouts/templates',
        name: 'layout-templates',
        component: () => import('@/views/LayoutTemplatesView.vue'),
        meta: { permission: 'layout:read' },
      },
      {
        path: 'layouts/:id',
        name: 'layout-editor',
        component: () => import('@/views/LayoutEditorView.vue'),
        meta: { permission: 'layout:read' },
        props: true,
      },
      {
        path: 'playlists',
        name: 'playlists',
        component: () => import('@/views/PlaylistsView.vue'),
        meta: { permission: 'playlist:read' },
      },
      {
        path: 'playlists/:id',
        name: 'playlist-editor',
        component: () => import('@/views/PlaylistEditorView.vue'),
        meta: { permission: 'playlist:read' },
        props: true,
      },
      {
        path: 'schedules',
        name: 'schedules',
        component: () => import('@/views/SchedulesView.vue'),
        meta: { permission: 'schedule:read' },
      },
      {
        path: 'schedules/:id',
        name: 'schedule-editor',
        component: () => import('@/views/ScheduleEditorView.vue'),
        meta: { permission: 'schedule:read' },
        props: true,
      },
      {
        path: 'screens',
        name: 'screens',
        component: () => import('@/views/ScreensView.vue'),
        meta: { permission: 'screen:read' },
      },
      {
        path: 'screens/:id',
        name: 'screen-detail',
        component: () => import('@/views/ScreenDetailView.vue'),
        meta: { permission: 'screen:read' },
        props: true,
      },
      {
        path: 'screen-groups',
        name: 'screen-groups',
        component: () => import('@/views/ScreenGroupsView.vue'),
        meta: { permission: 'screen:read' },
      },
      {
        path: 'programme',
        name: 'programme',
        component: () => import('@/views/ProgrammeView.vue'),
        meta: { permission: 'performance:read' },
      },
      {
        path: 'programme/feeds',
        name: 'performance-feeds',
        component: () => import('@/views/PerformanceFeedsView.vue'),
        meta: { permission: 'performance-feed:read' },
      },
      {
        path: 'programme/rules',
        name: 'performance-rules',
        component: () => import('@/views/PerformanceRulesView.vue'),
        meta: { permission: 'performance-rule:read' },
      },
      {
        path: 'programme/locations',
        name: 'performance-locations',
        component: () => import('@/views/PerformanceLocationsView.vue'),
        meta: { permission: 'performance:read' },
      },
      {
        path: 'programme/sponsors',
        name: 'house-sponsors',
        component: () => import('@/views/HouseSponsorsView.vue'),
        meta: { permission: 'performance:read' },
      },
      {
        path: 'signals',
        name: 'signals',
        component: () => import('@/views/SignalsView.vue'),
        meta: { permission: 'signal:read' },
      },
      {
        path: 'device-software',
        name: 'device-software',
        component: () => import('@/views/DeviceSoftwareView.vue'),
        meta: { permission: 'device:read' },
      },
      {
        path: 'integration-keys',
        name: 'integration-keys',
        component: () => import('@/views/IntegrationKeysView.vue'),
        meta: { permission: 'rolegrant:read' },
      },
      {
        path: 'security',
        name: 'security',
        component: () => import('@/views/SecurityView.vue'),
        // No `permission`, for the same reason access-tokens has none and a
        // stronger one: every action on this page acts on the caller's own
        // account, and all but one of them make it harder to use. Gating it
        // would only stop someone from securing an account they already hold.
      },
      {
        path: 'access-tokens',
        name: 'access-tokens',
        component: () => import('@/views/AccessTokensView.vue'),
        // Deliberately no `permission` (D20): a token carries its holder's own
        // authority and nothing more, so managing one's own is not an
        // administrative act. The org-wide table inside the page is what
        // checks `member:read`.
      },
      {
        path: 'approvals',
        name: 'approvals',
        component: () => import('@/views/ApprovalsView.vue'),
        meta: { permission: 'approval:approve' },
      },
      {
        path: 'preview/:screenId',
        name: 'preview',
        component: () => import('@/views/SchedulePreviewView.vue'),
        meta: { permission: 'schedule:preview' },
        props: true,
      },
      {
        path: 'overview',
        name: 'overview',
        component: () => import('@/views/OverviewView.vue'),
        meta: { permission: 'screen:read' },
      },
    ],
  },
  { path: '/:pathMatch(.*)*', redirect: '/' },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

/**
 * UG9 — the routes a group-scoped session may reach. The nav already hides
 * everything else, but a bookmark, a stale tab or a deep link would otherwise
 * still land such a user on the dashboard, which carries no permission of its
 * own and would render an empty, confusing shell.
 *
 * Reachable rather than forbidden: these users are not being denied something
 * they had, so sending them to Forbidden would misdescribe what happened. They
 * are redirected to their playlists, which is their home.
 */
const CONTENT_SCOPED_ROUTES = new Set([
  'media',
  'media-detail',
  'playlists',
  'playlist-editor',
  // A group-scoped user is precisely who the CLI is for — the venue lead who
  // may author in their own folders and nothing else — and D20 gives every
  // user their own tokens. Without this entry the guard would bounce them to
  // /playlists from a page that has no permission gate, so the one class of
  // user that cannot mint a token would be the one the feature was built for.
  // Safe to admit: the page shows only their own rows, and the org-wide table
  // still costs `member:read`, which a group grant does not confer.
  'access-tokens',
  // Same argument as access-tokens, and the mandate makes it load-bearing: a
  // group-scoped user confined by `requireMfa` who could not reach this page
  // would have no way to comply and no way into the product.
  'security',
  'mfa-setup',
  'forbidden',
  'login',
  'sso-callback',
]);

/**
 * Routes a session confined by the house's two-factor mandate may still reach
 * (M6). The client-side mirror of `MFA_SETUP_ALLOWED` in the API's
 * route-coverage test — the server is what actually enforces this, and every
 * other route would 403; the guard exists so the user meets an enrollment page
 * instead of an error.
 */
const MFA_SETUP_ROUTES = new Set(['mfa-setup', 'login', 'sso-callback']);

router.beforeEach((to) => {
  const auth = useAuthStore();
  if (to.meta.requiresAuth !== false && !auth.isAuthenticated) {
    return { name: 'login', query: { next: to.fullPath } };
  }
  if (to.name === 'login' && auth.isAuthenticated) {
    if (auth.mfaSetupRequired) return { name: 'mfa-setup' };
    return auth.isContentScoped ? { name: 'playlists' } : { name: 'dashboard' };
  }
  // Checked before the permission and content-scope rules below, because it
  // outranks both: a confined session's permissions are irrelevant while the
  // API is refusing every route they would authorize.
  if (
    auth.isAuthenticated &&
    auth.mfaSetupRequired &&
    typeof to.name === 'string' &&
    !MFA_SETUP_ROUTES.has(to.name)
  ) {
    return { name: 'mfa-setup' };
  }
  // The converse: once enrolled, the setup screen has nothing left to do, and
  // leaving it reachable strands anyone who bookmarks it.
  if (to.name === 'mfa-setup' && !auth.mfaSetupRequired) {
    return { name: 'dashboard' };
  }
  if (auth.isContentScoped && typeof to.name === 'string' && !CONTENT_SCOPED_ROUTES.has(to.name)) {
    return { name: 'playlists' };
  }
  if (to.meta.permission && !auth.hasPermission(to.meta.permission)) {
    return { name: 'forbidden' };
  }
});
